import type { ReaderNoyau, ReaderNoyauCard } from '@/components/reader';
import type {
  ExperiencePageDraft,
  NoyauBlockMode,
  NoyauCode,
  NoyauTextsContent,
  NoyauTextsDetail,
  RichText,
  VersionPin,
} from '@matter_extractor/shared';
import {
  NOYAU_CODES,
  isNoyauCode,
  noyauChoicePair,
  noyauOf,
  noyauTextFor,
  pageNoyauChoiceOf,
  pageShowsPortrait,
} from '@matter_extractor/shared';

/**
 * WHAT THE PREVIEWS SHOW of a Noyau block (noyau.md §3.2, N20; issue #151), in
 * pure functions: the page editor's preview, its « Aperçu avec » line, and the
 * full-screen preview of §9.7 all ask the same questions, and a card drawn two
 * ways would be two sequences.
 *
 * ## The real texts, for a pair of example (N20)
 *
 * « Ce n'est pas une valeur inventée : les textes existent, seule la paire est
 * simulée. » The texts are the version the reference of the experience points
 * at (`noyauTexts`, §3.5); the PAIR is the operator's choice in the inspector,
 * by default the first two noyaux of the table. The retained noyau of the
 * simulation is the principal of that pair - the person took the principal -
 * unless something says otherwise: the card taken in the full-screen preview,
 * or the noyau the bar « Simuler » fixes. Without any version, two cards
 * « Aucun texte encore »; while the texts are READ, « Lecture des textes… » -
 * an empty state shown for the length of a request would say they are missing.
 *
 * ## « Simuler : Aucun » shows no noyau at all (review of !464)
 *
 * No retained noyau is what a SKIPPED sequence leaves (§4.5): a person never
 * sees these blocks, so the principal's portrait would be a page nobody reads.
 * `NO_RETAINED_NOYAU` says it, and the blocks draw the reader's empty frames.
 *
 * ## The identifier of a preview's card IS its noyau
 *
 * N28 is about the PERSON's browser: the server deals opaque identifiers for a
 * session. A preview is the operator's own screen, which already names the
 * pair above the frame, so a card is identified by its triplet - which is what
 * lets the full-screen preview read the noyau of the card taken back without a
 * table beside it (`previewNoyauOfCard`).
 */

/** The pair « Aperçu avec » shows (N20): a principal and a second. */
export type NoyauPreviewPair = {
  readonly principal: NoyauCode;
  readonly second: NoyauCode;
};

/** « par défaut les deux premiers noyaux de la table » (N20). */
export const DEFAULT_NOYAU_PREVIEW_PAIR: NoyauPreviewPair = {
  principal: NOYAU_CODES[0],
  second: NOYAU_CODES[1],
};

/**
 * The pair after one of its two selects moved: the other one gives way when
 * the two would name the same noyau - a choice between a text and itself shows
 * nothing - and takes the value the moved one left.
 */
export function movedPair(
  pair: NoyauPreviewPair,
  side: keyof NoyauPreviewPair,
  code: NoyauCode,
): NoyauPreviewPair {
  if (side === 'principal') {
    return { principal: code, second: pair.second === code ? pair.principal : pair.second };
  }
  return { principal: pair.principal === code ? pair.second : pair.principal, second: code };
}

/** What a card of a preview says without any version of the texts (N20). */
export const NO_NOYAU_TEXT = 'Aucun texte encore';

/** What a card of a preview says while the texts are still being read. */
export const READING_NOYAU_TEXT = 'Lecture des textes…';

function paragraph(words: string): RichText {
  return {
    nodes: [
      { type: 'paragraph', children: [{ type: 'text', text: words, marks: [], color: null }] },
    ],
  };
}

const NO_TEXT = paragraph(NO_NOYAU_TEXT);
const READING = paragraph(READING_NOYAU_TEXT);

/**
 * The texts a preview shows: a version's content, null when there is none to
 * read under the tag (no version, nothing in production), undefined while it
 * is being read.
 */
export type PreviewNoyauTexts = NoyauTextsContent | null | undefined;

/** « Simuler : Aucun » - no noyau retained, the sequence skipped (see the head). */
export const NO_RETAINED_NOYAU = 'none';

/**
 * The retained noyau a preview plays under: a noyau - a card taken, a value of
 * the bar « Simuler » -, null when nothing fixes one (the principal of the pair
 * then stands, N20), or `NO_RETAINED_NOYAU`.
 */
export type PreviewRetained = NoyauCode | null | typeof NO_RETAINED_NOYAU;

/**
 * The retained noyau of a simulation: the one given - a card taken, a value of
 * the bar « Simuler » - or the principal of the pair.
 */
function simulatedRetained(pair: NoyauPreviewPair, retained: NoyauCode | null): NoyauCode {
  return retained ?? pair.principal;
}

/** The text a preview shows, or what stands in its place while there is none to show. */
function shownText(texts: PreviewNoyauTexts, noyau: NoyauCode, mode: NoyauBlockMode): RichText {
  if (texts === undefined) return READING;
  return texts === null ? NO_TEXT : noyauTextFor(texts, noyau, mode);
}

/**
 * The two noyaux a block of choice of the preview plays, in the order of the
 * pair (§5.1: a session DRAWS the order, a preview shows it as chosen).
 */
export function previewChoicePair(
  mode: Exclude<NoyauBlockMode, 'portrait'>,
  pair: NoyauPreviewPair,
  retained: NoyauCode | null,
): readonly [NoyauCode, NoyauCode] {
  const played = noyauChoicePair(mode, {
    principal: pair.principal,
    second: pair.second,
    retained: simulatedRetained(pair, retained),
  });
  // `noyauChoicePair` answers null only for a missing half, which a preview
  // never lacks: both halves are simulated.
  return played ?? [pair.principal, pair.second];
}

/** A card of a preview: its noyau for an identifier (see the head), the real text or none. */
function card(texts: PreviewNoyauTexts, noyau: NoyauCode, mode: NoyauBlockMode): ReaderNoyauCard {
  return { cardId: noyau, text: shownText(texts, noyau, mode) };
}

/**
 * What the Noyau blocks of ONE page show in a preview: the cards of every
 * block of choice it carries, and the portrait of the simulated retained noyau
 * - or nothing of any noyau under « Simuler : Aucun », the reader's empty
 * frames standing where a skipped sequence shows nobody anything.
 */
export function previewNoyau(
  page: ExperiencePageDraft,
  texts: PreviewNoyauTexts,
  pair: NoyauPreviewPair,
  retained: PreviewRetained,
): ReaderNoyau {
  if (retained === NO_RETAINED_NOYAU) return { choices: {}, portrait: null };
  const choices: Record<string, readonly [ReaderNoyauCard, ReaderNoyauCard]> = {};
  for (const block of page.temps.flatMap((temps) => temps.blocks)) {
    if (block.type !== 'noyau' || block.mode === 'portrait') continue;
    const [one, other] = previewChoicePair(block.mode, pair, retained);
    choices[block.id] = [card(texts, one, block.mode), card(texts, other, block.mode)];
  }
  return {
    choices,
    portrait: shownText(texts, simulatedRetained(pair, retained), 'portrait'),
  };
}

/** The noyau a card of a preview stands for - its identifier (see the head) - or null. */
export function previewNoyauOfCard(cardId: string): NoyauCode | null {
  return isNoyauCode(cardId) ? cardId : null;
}

/**
 * « Aperçu avec la paire Souverain Magicien / Souverain Juriste » - the line
 * over the preview of a page that carries a Noyau block (maquette
 * `EditeurPageNoyau`), or null on a page that carries none. A page of choice
 * names its pair, a page of portrait the one noyau it shows - and under
 * « Simuler : Aucun », the line says why the frames are empty.
 */
export function previewPairLine(
  page: ExperiencePageDraft,
  pair: NoyauPreviewPair,
  retained: PreviewRetained,
): { readonly lead: string; readonly noyaux: readonly NoyauCode[] } | null {
  const choice = pageNoyauChoiceOf(page);
  if (retained === NO_RETAINED_NOYAU) {
    return choice !== null || pageShowsPortrait(page)
      ? { lead: 'Aperçu sans noyau retenu : la séquence est sautée', noyaux: [] }
      : null;
  }
  if (choice !== null) {
    return { lead: 'Aperçu avec la paire', noyaux: previewChoicePair(choice.mode, pair, retained) };
  }
  if (pageShowsPortrait(page)) {
    return { lead: 'Aperçu avec le noyau', noyaux: [simulatedRetained(pair, retained)] };
  }
  return null;
}

/** « Souverain Magicien » - what an option of « Aperçu avec » says. */
export function noyauName(code: NoyauCode): string {
  return noyauOf(code).name;
}

/* ------------------------------------------------------ the state of the texts */

/**
 * « Version 2 en production, version 3 en développement » - the state of the
 * texts beside the inspector's tag (maquette `EditeurPageNoyau`), read off the
 * detail of the singleton, and the empty state of §6.4 when there is no
 * version at all. The caller draws nothing while the detail is in flight.
 */
export function noyauTextsStateLine(detail: NoyauTextsDetail | null): string {
  if (detail === null) {
    return 'Aucune version : aucune session de cette expérience ne démarre tant que les textes ne sont pas enregistrés.';
  }
  const development = `version ${detail.development.number} en développement`;
  if (detail.production === null) return `Rien en production, ${development}`;
  if (detail.production.id === detail.development.id) {
    return `Version ${detail.production.number} en production et en développement`;
  }
  return `Version ${detail.production.number} en production, ${development}`;
}

/**
 * What an editor has READ of the texts of the noyaux (`useNoyauTextsSource`):
 * the detail of the singleton - null while no version exists at all (§6.4),
 * undefined while it is read - and the content of the PINNED version when the
 * reference pins a number that is not the development one, undefined otherwise
 * or while it is read.
 */
export type NoyauTextsSource = {
  readonly detail: NoyauTextsDetail | null | undefined;
  readonly pinned: NoyauTextsContent | undefined;
};

/** Nothing read yet - what a surface that reads no text hands (a test, a vignette). */
export const NO_NOYAU_TEXTS: NoyauTextsSource = { detail: undefined, pinned: undefined };

/**
 * The texts a reference READS at its pin (§3.5, vitrine.md §3.1): `latest` is
 * the development version, whose content is in the detail, and so is a pin on
 * that very number; any other number is the separate read. Null when the texts
 * hold no version at all or not the one pinned, and undefined while what is
 * needed is still in flight.
 */
export function noyauTextsAt(
  source: NoyauTextsSource,
  pin: VersionPin,
): NoyauTextsContent | null | undefined {
  const { detail } = source;
  if (detail === undefined) return undefined;
  if (detail === null) return null;
  if (pin === 'latest' || pin === detail.development.number) return detail.developmentContent;
  if (!detail.versions.some((version) => version.number === pin)) return null;
  return source.pinned;
}
